import ApiServer from '@/utils/api/ApiServer.js'
import { get } from 'axios'
import { azureMapsApiKey } from '@/utils/map/MapHelpers.js'

let accountsRequestInFlight = null

const requestAccounts = async (context, tags) => {
  context.commit('setLoading', true)
  try {
    let tagFilter = ''
    if (tags) {
      tagFilter = (tags.map(tag => tag.value)).toString()
    }
    const accountResponse = await ApiServer.get('accounts', {
      headers: {
        tagsFilter: tagFilter
      }
    })
    context.commit('setAccounts', accountResponse.data)
    return accountResponse.data
  } finally {
    context.commit('setLoading', false)
  }
}

export default {
  fetchAccounts (context, tags) {
    if (tags) return requestAccounts(context, tags)
    if (!accountsRequestInFlight) {
      accountsRequestInFlight = requestAccounts(context).finally(() => { accountsRequestInFlight = null })
    }
    return accountsRequestInFlight
  },
  async deleteAccount (_, accountId) {
    const response = await ApiServer.delete(`accounts/${accountId}`)
    return response
  },
  async createAccount (_, account) {
    const response = await ApiServer.post('accounts', account)
    return response.data
  },
  async updateAccount (_, account) {
    const response = await ApiServer.put(`accounts/${account.accountId}`, account)
    return response.data
  },

  async patchAccount (_, { accountId, modifiedAt, body }) {
    const headers = modifiedAt ? { modifiedAt } : {}
    const response = await ApiServer.patch(`accounts/${accountId}`, body, { headers })
    return response.data
  },

  async fetchAccountById (_, accountId) {
    const path = `accounts/${accountId}`
    const response = await ApiServer.get(path)
    return response.data
  },

  async fetchAddressToAccount (_, accountId) {
    const path = `accounts/${accountId}/addresses`
    const response = await ApiServer.get(path)
    return response.data
  },

  async addAddressToAccount (_, createRequest) {
    const path = `accounts/${createRequest.accountId}/addresses`
    const response = await ApiServer.post(path, createRequest.address)
    return response.data
  },

  async deleteAccountAddress (_, createRequest) {
    const { accountId, accountAddressId } = createRequest
    const path = `accounts/${accountId}/addresses/${accountAddressId}`
    const response = await ApiServer.delete(path)
    return response.data
  },

  async updateAccountAddress (_, createRequest) {
    const { accountId, address } = createRequest
    const path = `accounts/${accountId}/addresses/${address.accountAddressId}`
    const response = await ApiServer.put(path, createRequest.address)
    return response.data
  },

  async getAccountsWithTags (_, tagArray) {
    const tagValues = (tagArray.map(tag => tag.value)).toString()
    const response = await ApiServer.get('accounts', {
      headers: {
        tagsFilter: tagValues
      }
    })
    return response.data
  },

  async validateAddress (_, companyAddress) {
    const format = 'JSON'
    const apiVersion = '1.0'
    const requestURL = `https://atlas.microsoft.com/search/address/${format}?api-version=${apiVersion}&subscription-key=${azureMapsApiKey()}&query=${companyAddress}`
    const encodedURL = encodeURI(requestURL)
    const { data } = await get(encodedURL)
    return data
  },

  // Payables
  async fetchAccountPayables (_, accountId) {
    const response = await ApiServer.get(`accounts/${accountId}/accountpayables`)
    return response.data
  },

  async fetchAccountPayableById (_, { accountId, accountPayableId }) {
    const response = await ApiServer.get(`accounts/${accountId}/accountpayables/${accountPayableId}`)
    return response.data
  },

  async createAccountPayable (_, { accountId, accountPayable }) {
    const response = await ApiServer.post(`accounts/${accountId}/accountpayables`, accountPayable)
    return response.data
  },

  async updateAccountPayable (_, { accountId, accountPayable }) {
    accountPayable.accountId = accountId
    const response = await ApiServer.put(`accounts/${accountId}/accountPayables/${accountPayable.accountPayableId}`, accountPayable)
    return response.data
  },

  async deleteAccountPayable (_, { accountId, accountPayableId }) {
    const response = await ApiServer.delete(`accounts/${accountId}/accountPayables/${accountPayableId}`)
    return response.data
  },

  async fetchAccountPayableTemplates (_, accountId) {
    const response = await ApiServer.get(`accounts/${accountId}/accountpayabletemplates`)
    return response.data
  },

  async createAccountPayableTemplate (_, { accountId, accountPayableTemplate }) {
    const response = await ApiServer.post(`accounts/${accountId}/accountpayabletemplates`, accountPayableTemplate)
    return response.data
  },

  async updateAccountPayableTemplate (_, { accountId, accountPayableTemplate }) {
    const response = await ApiServer.put(`accounts/${accountId}/accountpayabletemplates/${accountPayableTemplate.accountPayableTemplateId}`, accountPayableTemplate)
    return response.data
  },

  async deleteAccountPayableTemplate (_, { accountId, accountPayableTemplateId }) {
    const response = await ApiServer.delete(`accounts/${accountId}/accountpayabletemplates/${accountPayableTemplateId}`)
    return response.data
  },

  async patchAccountPayableTemplate (_, { accountId, accountPayableTemplateId, modifiedAt, body }) {
    const headers = modifiedAt ? { modifiedAt } : {}
    const response = await ApiServer.patch(`accounts/${accountId}/accountpayabletemplates/${accountPayableTemplateId}`, body, { headers })
    return response.data
  },

  // Overweight Penalties
  async fetchAccountPenaltyDefinition (_, accountId) {
    const response = await ApiServer.get(`accounts/${accountId}/penaltydefinitions`)
    return response.data
  },

  async createAccountPenaltyDefinition (_, { accountId, penaltyDefinition }) {
    const response = await ApiServer.post(`accounts/${accountId}/penaltydefinitions`, penaltyDefinition)
    return response.data
  },

  async deleteAccountPenaltyDefinition (_, accountId) {
    const response = await ApiServer.delete(`accounts/${accountId}/penaltydefinitions`)
    return response.data
  },

  async createAccountPenaltyThreshold (_, { accountId, thresholdDefinition }) {
    const response = await ApiServer.post(`accounts/${accountId}/penaltydefinitions/penaltythresholds`, thresholdDefinition)
    return response.data
  },

  async deleteAccountPenaltyThreshold (_, { accountId, thresholdId }) {
    const response = await ApiServer.delete(`accounts/${accountId}/penaltydefinitions/penaltythresholds/${thresholdId}`)
    return response.data
  },

  // Ticket Books
  async fetchTicketBooks (_, { accountId, includeInUse = false, includeArchived = false, includeAvailable = true }) {
    const response = await ApiServer.get(`accounts/${accountId}/ticketbooks?includeInUse=${includeInUse}&includeArchived=${includeArchived}&includeAvailable=${includeAvailable}`)
    return response.data.map(tb => ({ ...tb, accountId }))
  },

  async createTicketBook (_, { accountId, ticketBook }) {
    const response = await ApiServer.post(`accounts/${accountId}/ticketbooks`, ticketBook)
    return response.data
  },

  async deleteTicketBook (_, { accountId, ticketBookId }) {
    const response = await ApiServer.delete(`accounts/${accountId}/ticketbooks/${ticketBookId}`)
    return response.data
  },

  async updateTicketBook (_, { accountId, ticketBook }) {
    const response = await ApiServer.put(`accounts/${accountId}/ticketbooks/${ticketBook.ticketBookId}`, ticketBook)
    return response.data
  },

  async fetchTicketBookPages (_, { accountId, ticketBookId }) {
    const response = await ApiServer.get(`accounts/${accountId}/ticketbooks/${ticketBookId}/pages`)
    return response.data
  },

  async createTicketBookFromSource (_, { accountId, sourceTicketBookId, ticketBook }) {
    const response = await ApiServer.post(`accounts/${accountId}/ticketbooks/${sourceTicketBookId}`, ticketBook)
    return response.data
  },

  async addTicketBookPages (_, { accountId, ticketBookId, pages }) {
    const response = await ApiServer.post(`accounts/${accountId}/ticketbooks/${ticketBookId}/pages`, pages)
    return response.data
  }
}
